import api from './api';

export type MerchantKycStatus =
  | 'in_progress'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'completed';

export interface MerchantKycRow {
  id: number;
  userId: number;
  restaurantId?: number | null;
  status: MerchantKycStatus;
  currentStep: number;
  selectedPackageId?: number | null;
  packageName?: string | null;
  packagePrice?: number | null;
  packagePaymentStatus?: string;
  packagePaidAt?: string | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectionReason?: string | null;
  merchantName?: string;
  merchantPhone?: string;
  merchantEmail?: string;
  storeName?: string;
  isListed?: boolean;
}

export interface MerchantKycOpenHourDay {
  dayOfWeek?: number;
  label?: string;
  enabled?: boolean;
  startTime?: string;
  endTime?: string;
}

export interface MerchantKycStepData {
  start?: { acknowledged?: boolean };
  create_shop?: { acknowledged?: boolean };
  region?: {
    provinceId?: string;
    provinceName?: string;
  };
  basic_info?: {
    storeName?: string;
    contactPhone?: string;
    provinceId?: string;
    provinceName?: string;
    districtId?: string;
    districtName?: string;
    wardId?: string;
    wardName?: string;
    street?: string;
    fullAddress?: string;
    lat?: number;
    lng?: number;
  };
  representative?: {
    fullName?: string;
    email?: string;
    phone?: string;
    idNumber?: string;
    idIssueDate?: string;
    idIssuePlace?: string;
    idFrontImageUrl?: string;
    idBackImageUrl?: string;
    idFrontImagePreviewUrl?: string;
    idBackImagePreviewUrl?: string;
  };
  bank?: {
    bankCode?: string;
    bankName?: string;
    accountNumber?: string;
    accountName?: string;
    branch?: string;
  };
  hours?: {
    days?: MerchantKycOpenHourDay[];
  };
  photos?: {
    avatarUrl?: string;
    coverUrl?: string;
    avatarPreviewUrl?: string;
    coverPreviewUrl?: string;
  };
  package_intro?: { acknowledged?: boolean };
  package_select?: { packageId?: number };
  preview_contract?: {
    accepted?: boolean;
    contractVersion?: string;
  };
  success_payment?: {
    acknowledged?: boolean;
    paymentCompleted?: boolean;
    paymentStatus?: string;
    orderId?: string;
    transactionId?: string;
  };
}

export interface MerchantKycDetail {
  userId: number;
  restaurantId?: number | null;
  status: MerchantKycStatus;
  currentStep: number;
  totalSteps: number;
  stepData: MerchantKycStepData;
  stepKeys?: Record<number, string>;
  isComplete?: boolean;
  canAccessApp?: boolean;
  isMarketplaceLive?: boolean;
  selectedPackageId?: number | null;
  packageName?: string | null;
  packagePrice?: number | null;
  packagePaymentStatus?: string;
  packagePaidAt?: string | null;
  contractAcceptedAt?: string | null;
  contractVersion?: string | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectionReason?: string | null;
  merchantName?: string;
  merchantPhone?: string;
  merchantEmail?: string;
  storeName?: string;
  isListed?: boolean;
}

export async function fetchMerchantKycList(params: {
  page?: number;
  limit?: number;
  status?: MerchantKycStatus;
}) {
  const res = await api.get('/admin/merchant-onboarding', { params });
  return res.data as {
    success: boolean;
    data: MerchantKycRow[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    message?: string;
  };
}

export async function fetchMerchantKycDetail(userId: number) {
  const res = await api.get(`/admin/merchant-onboarding/${userId}`);
  return res.data as {
    success: boolean;
    data?: MerchantKycDetail;
    message?: string;
  };
}

export async function approveMerchantKyc(userId: number) {
  const res = await api.post(`/admin/merchant-onboarding/${userId}/approve`);
  return res.data as {
    success: boolean;
    message?: string;
    data?: MerchantKycRow;
    merchantId?: number;
  };
}

export async function rejectMerchantKyc(userId: number, reason?: string) {
  const res = await api.post(`/admin/merchant-onboarding/${userId}/reject`, {
    reason,
  });
  return res.data;
}

export async function skipMerchantKycPackagePayment(
  merchantId: number | string
) {
  const paths = [
    `/merchants/${merchantId}/skip-kyc-package-payment`,
    `/admin/merchants/${merchantId}/skip-kyc-package-payment`,
    `/admin/merchant-onboarding/${merchantId}/skip-package-payment`,
  ];

  let lastError: unknown;
  for (const path of paths) {
    try {
      const res = await api.post(path);
      return res.data as {
        success: boolean;
        message?: string;
        data?: {
          isListed?: boolean;
          userId?: number;
          restaurantId?: number;
        };
      };
    } catch (err: unknown) {
      lastError = err;
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status !== 404) {
        throw err;
      }
    }
  }

  throw lastError;
}
